import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { Label } from "./Label";
import { Tooltip } from "./Tooltip";
export const checkboxStyles = cva(cn("h-4 w-4 rounded-md relative shrink-0 peer border transition duration-100 ease-out motion-reduce:transition-none", "active:scale-95", "border-border-dark bg-background", "text-foreground", "focus-visible:ring-ring ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2", "disabled:cursor-not-allowed disabled:opacity-50 disabled:border-border-dark disabled:bg-background"));
// The checked state renders a dark rounded square that fills the box's inner
// content area; the light "ring" is the box's own border showing through.
// Concentric corners: inner radius = outer radius (rounded-md, 6px) minus the
// 1px border the fill sits inside.
export const checkboxIndicatorStyles = cva(cn("absolute inset-0 flex items-center justify-center rounded-[5px]", 
// Quick pop-in on check; unchecking unmounts instantly, which is fine —
// exits may be faster than entrances, and this is a high-frequency control.
"animate-in fade-in-0 zoom-in-90 duration-150 ease-enter motion-reduce:animate-none"), {
    variants: {
        isMutedAfterCheck: {
            true: "bg-faint/50",
            false: "bg-foreground",
        },
    },
    defaultVariants: {
        isMutedAfterCheck: false,
    },
});
const Checkbox = React.forwardRef(({ className, checked, id, tooltip, isMutedAfterCheck, ...props }, ref) => {
    const checkbox = (React.createElement(CheckboxPrimitive.Root, { ref: ref, id: id, className: cn(checkboxStyles(), className), checked: checked === "partial" ? "indeterminate" : checked, ...props },
        React.createElement(CheckboxPrimitive.Indicator, { className: checkboxIndicatorStyles({ isMutedAfterCheck }) }, checked === "partial" ? (React.createElement(Minus, { className: "h-3 w-3 text-background" })) : (React.createElement(Check, { className: "h-3 w-3 text-background" })))));
    return tooltip ? (React.createElement(Tooltip, { label: tooltip, trigger: checkbox, tooltipTriggerAsChild: true })) : (checkbox);
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
function CheckboxWithText({ text, tooltip, id: idProp, ...props }) {
    // Unique id per instance so checkbox and label stay associated (htmlFor/id); required for a11y and click-label-to-toggle.
    const generatedId = React.useId();
    const id = idProp !== null && idProp !== void 0 ? idProp : generatedId;
    const content = (React.createElement("div", { className: "items-top flex items-center space-x-2" },
        React.createElement(Checkbox, { id: id, ...props }),
        React.createElement(Label, { htmlFor: id, className: "cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" }, text)));
    return tooltip ? React.createElement(Tooltip, { label: tooltip, trigger: content }) : content;
}
function CheckBoxWithTextAndDescription({ text, description, tooltip, id: idProp, ...props }) {
    // Unique id per instance so checkbox and label stay associated (htmlFor/id); required for a11y and click-label-to-toggle.
    const generatedId = React.useId();
    const id = idProp !== null && idProp !== void 0 ? idProp : generatedId;
    const content = (React.createElement("div", { className: "items-top flex space-x-2" },
        React.createElement(Checkbox, { id: id, ...props }),
        React.createElement("div", { className: "grid gap-1.5 leading-none" },
            React.createElement(Label, { htmlFor: id, className: "cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70" }, text),
            React.createElement("p", { className: cn("text-xs", "text-muted-foreground") }, description))));
    return tooltip ? React.createElement(Tooltip, { label: tooltip, trigger: content }) : content;
}
export { CheckBoxWithTextAndDescription, Checkbox, CheckboxWithText };
//# sourceMappingURL=Checkbox.js.map